import { REPORT_SECTION_LABELS } from '~/modules/tools/views/PlagiarismCheckerV4/report/reportStructure';
import { formatReportPercentage } from '~/modules/tools/views/PlagiarismCheckerV4/report/utils/formatters';

type SummaryInput = {
  overall_similarity_pct: number;
  needs_attention_pct: number;
  properly_attributed_pct: number;
  exclude_references: boolean;
};

export type SummarySegment = {
  text: string;
  bold?: boolean;
};

export const generateCitationAwareSummarySegments = ({
  overall_similarity_pct,
  needs_attention_pct,
  properly_attributed_pct,
  exclude_references,
}: SummaryInput): SummarySegment[] => {
  if (overall_similarity_pct === 0) {
    return [
      {
        text: 'In plain words: we found no matches with sources on the web or in the JustDone database. Your text appears fully original.',
      },
    ];
  }

  const segments: SummarySegment[] = [
    { text: 'In plain words: about ' },
    { text: `${formatReportPercentage(overall_similarity_pct)}%`, bold: true },
    {
      text: ' of your text matches sources we found on the web and in the JustDone database.',
    },
  ];

  if (needs_attention_pct > 0) {
    segments.push(
      { text: ' Of that, ' },
      {
        text: `${formatReportPercentage(needs_attention_pct)}% needs`,
        bold: true,
      },
      {
        text: " attention — it's copied without a citation or quotation marks, so add a source or rewrite it in your own words.",
      },
    );
  }

  if (properly_attributed_pct > 0) {
    segments.push(
      { text: ' The other ' },
      {
        text: `${formatReportPercentage(properly_attributed_pct)}% is fine`,
        bold: true,
      },
      {
        text: " — you've already quoted and credited it correctly, so it doesn't count against you.",
      },
    );
  }

  if (exclude_references) {
    segments.push({
      text: ' Your reference list was excluded from the score.',
    });
  }

  segments.push({
    text: ` See exactly what to fix in the "${REPORT_SECTION_LABELS.HIGHLIGHTED_TEXT}" section of this report.`,
  });

  return segments;
};

export const generateCitationAwareSummary = (input: SummaryInput): string => {
  return generateCitationAwareSummarySegments(input)
    .map((segment) => segment.text)
    .join('');
};
